import React, { useState, useEffect, useCallback } from 'react';
import {
  Camera, Zap, Car, Plus, RefreshCw, Shield, AlertTriangle, CheckCircle,
  Activity, Wifi, Search, Eye, Trash2,
  Network, Bug, Radar,
} from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';
import { authFetch } from '../utils/authFetch';
import SurveillanceNetFlowTab from './surveillance/SurveillanceNetFlowTab';
import SurveillanceSecurityTab from './surveillance/SurveillanceSecurityTab';
import RegisterDeviceModal from './surveillance/RegisterDeviceModal';
import DiscoveryTab from './surveillance/DiscoveryTab';
import DeviceDetailPanel from './surveillance/DeviceDetailPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get owner ID - uses auth context or falls back to session storage
const getOwnerId = (user) => {
  if (user?.id) return user.id;
  if (user?.email) return user.email;
  // Fallback to session-based ID for demo purposes
  let sessionId = sessionStorage.getItem('securesphere_owner_id');
  if (!sessionId) {
    sessionId = `user-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('securesphere_owner_id', sessionId);
  }
  return sessionId;
};

const SurveillanceIoTDashboard = () => {
  // Get user from auth context
  const { user } = useAuth() || {};
  const ownerId = getOwnerId(user);
  
  const [activeTab, setActiveTab] = useState('devices');
  const [activeCategory, setActiveCategory] = useState('cameras');
  const [devices, setDevices] = useState([]);
  const [overview, setOverview] = useState(null);
  const [categoryData, setCategoryData] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [monitoringData, setMonitoringData] = useState(null);
  const [netflowStats, setNetflowStats] = useState(null);
  
  // New state for enhanced features
  const [scanResults, setScanResults] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const [trafficPatterns, setTrafficPatterns] = useState(null);
  const [anomalies, setAnomalies] = useState(null);
  const [botnetStatus, setBotnetStatus] = useState(null);
  const [vulnerabilities, setVulnerabilities] = useState(null);
  const [collectorStatus, setCollectorStatus] = useState(null);

  // Category configurations
  const categories = [
    {
      id: 'cameras',
      name: 'Cameras & CCTV',
      icon: Camera,
      color: 'blue',
      description: 'IP cameras, CCTV systems, security cameras'
    },
    {
      id: 'smart_meters',
      name: 'Smart Meters',
      icon: Zap,
      color: 'yellow',
      description: 'Electricity, water, and gas meters'
    },
    {
      id: 'dashcams',
      name: 'Dashcams',
      icon: Car,
      color: 'green',
      description: 'Vehicle dashboard cameras'
    }
  ];

  // Registration form state - uses dynamic owner ID
  const [registerForm, setRegisterForm] = useState({
    // Common fields
    device_name: '',
    device_type: '',
    manufacturer: '',
    model: '',
    firmware_version: '1.0.0',
    location: '',
    // Camera specific
    ip_address: '',
    mac_address: '',
    resolution: '1080p',
    has_night_vision: true,
    has_ptz: false,
    has_audio: true,
    stream_protocol: 'rtsp',
    // Smart meter specific
    meter_id: '',
    communication_type: 'nb_iot',
    reading_interval_minutes: 15,
    utility_provider: '',
    // Dashcam specific
    vehicle_id: '',
    vehicle_type: 'car',
    license_plate: '',
    sim_iccid: '',
    has_gps: true,
    has_cloud_backup: true,
    storage_gb: 64
  });

  const deviceTypeOptions = {
    cameras: [
      { id: 'ip_camera', name: 'IP Camera', icon: Camera },
      { id: 'cctv', name: 'CCTV System', icon: Video },
      { id: 'ptz_camera', name: 'PTZ Camera', icon: Eye },
      { id: 'dome_camera', name: 'Dome Camera', icon: Globe },
      { id: 'bullet_camera', name: 'Bullet Camera', icon: Camera },
      { id: 'thermal_camera', name: 'Thermal Camera', icon: Activity },
      { id: 'doorbell_camera', name: 'Doorbell Camera', icon: Camera },
      { id: 'body_camera', name: 'Body Camera', icon: Camera }
    ],
    smart_meters: [
      { id: 'electricity_meter', name: 'Electricity Meter', icon: Zap },
      { id: 'water_meter', name: 'Water Meter', icon: Droplet },
      { id: 'gas_meter', name: 'Gas Meter', icon: Flame },
      { id: 'solar_meter', name: 'Solar Meter', icon: Sun },
      { id: 'ev_charger_meter', name: 'EV Charger Meter', icon: Battery },
      { id: 'industrial_meter', name: 'Industrial Meter', icon: Factory }
    ],
    dashcams: [
      { id: 'front_dashcam', name: 'Front Dashcam', icon: Car },
      { id: 'dual_dashcam', name: 'Dual Channel Dashcam', icon: Video },
      { id: 'fleet_camera', name: 'Fleet Camera', icon: Truck },
      { id: '360_dashcam', name: '360° Dashcam', icon: Globe },
      { id: 'taxi_camera', name: 'Taxi/Ride Camera', icon: Navigation },
      { id: 'bus_camera', name: 'Bus/Transit Camera', icon: Bus }
    ]
  };

  const manufacturers = {
    cameras: ['Hikvision', 'Dahua', 'Axis', 'Bosch', 'Hanwha', 'Uniview', 'Vivotek', 'Pelco', 'Sony', 'Panasonic'],
    smart_meters: ['Itron', 'Landis+Gyr', 'Honeywell', 'Kamstrup', 'Sensus', 'Elster', 'Secure Meters', 'HPL', 'Genus Power'],
    dashcams: ['Viofo', 'Thinkware', 'BlackVue', 'Garmin', 'Nextbase', 'Vantrue', 'VAVA', 'Rexing', 'REDTIGER']
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [overviewRes, devicesRes, alertsRes] = await Promise.all([
        authFetch(`${API_URL}/api/securesphere/surveillance-iot/dashboard/overview`),
        authFetch(`${API_URL}/api/securesphere/surveillance-iot/devices?category=${activeCategory}`),
        authFetch(`${API_URL}/api/securesphere/surveillance-iot/alerts?status=active`)
      ]);

      const overviewData = await overviewRes.json();
      const devicesData = await devicesRes.json();
      const alertsData = await alertsRes.json();

      setOverview(overviewData);
      setDevices(devicesData.devices || []);
      setAlerts(alertsData.alerts || []);

      // Fetch category-specific data
      const categoryRes = await authFetch(`${API_URL}/api/securesphere/surveillance-iot/dashboard/category/${activeCategory}`);
      const categoryData = await categoryRes.json();
      setCategoryData(categoryData);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [activeCategory]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const registerDevice = async (e) => {
    e.preventDefault();
    try {
      let endpoint = '';
      let payload = {};

      if (activeCategory === 'cameras') {
        endpoint = `${API_URL}/api/securesphere/surveillance-iot/cameras/register`;
        payload = {
          device_name: registerForm.device_name,
          device_type: registerForm.device_type,
          manufacturer: registerForm.manufacturer,
          model: registerForm.model,
          ip_address: registerForm.ip_address,
          mac_address: registerForm.mac_address,
          location: registerForm.location,
          resolution: registerForm.resolution,
          has_night_vision: registerForm.has_night_vision,
          has_ptz: registerForm.has_ptz,
          has_audio: registerForm.has_audio,
          stream_protocol: registerForm.stream_protocol,
          firmware_version: registerForm.firmware_version,
          owner_id: ownerId
        };
      } else if (activeCategory === 'smart_meters') {
        endpoint = `${API_URL}/api/securesphere/surveillance-iot/smart-meters/register`;
        payload = {
          device_name: registerForm.device_name,
          device_type: registerForm.device_type,
          manufacturer: registerForm.manufacturer,
          model: registerForm.model,
          meter_id: registerForm.meter_id,
          ip_address: registerForm.ip_address,
          location: registerForm.location,
          communication_type: registerForm.communication_type,
          reading_interval_minutes: registerForm.reading_interval_minutes,
          firmware_version: registerForm.firmware_version,
          utility_provider: registerForm.utility_provider,
          owner_id: ownerId
        };
      } else if (activeCategory === 'dashcams') {
        endpoint = `${API_URL}/api/securesphere/surveillance-iot/dashcams/register`;
        payload = {
          device_name: registerForm.device_name,
          device_type: registerForm.device_type,
          manufacturer: registerForm.manufacturer,
          model: registerForm.model,
          vehicle_id: registerForm.vehicle_id,
          vehicle_type: registerForm.vehicle_type,
          license_plate: registerForm.license_plate,
          sim_iccid: registerForm.sim_iccid,
          resolution: registerForm.resolution,
          has_gps: registerForm.has_gps,
          has_cloud_backup: registerForm.has_cloud_backup,
          storage_gb: registerForm.storage_gb,
          firmware_version: registerForm.firmware_version,
          owner_id: ownerId
        };
      }

      const response = await authFetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setShowRegisterModal(false);
        fetchData();
        resetForm();
      }
    } catch (error) {
    }
  };

  const resetForm = () => {
    setRegisterForm({
      device_name: '',
      device_type: '',
      manufacturer: '',
      model: '',
      firmware_version: '1.0.0',
      location: '',
      ip_address: '',
      mac_address: '',
      resolution: '1080p',
      has_night_vision: true,
      has_ptz: false,
      has_audio: true,
      stream_protocol: 'rtsp',
      meter_id: '',
      communication_type: 'nb_iot',
      reading_interval_minutes: 15,
      utility_provider: '',
      vehicle_id: '',
      vehicle_type: 'car',
      license_plate: '',
      sim_iccid: '',
      has_gps: true,
      has_cloud_backup: true,
      storage_gb: 64
    });
  };

  const viewDeviceDetails = async (device) => {
    setSelectedDevice(device);
    try {
      const [monitoringRes, netflowRes] = await Promise.all([
        authFetch(`${API_URL}/api/securesphere/surveillance-iot/monitoring/${device.id}`),
        authFetch(`${API_URL}/api/securesphere/surveillance-iot/netflow/stats/${device.id}`)
      ]);
      const monitoringData = await monitoringRes.json();
      const netflowData = await netflowRes.json();
      setMonitoringData(monitoringData);
      setNetflowStats(netflowData);
    } catch (error) {
    }
  };

  const scanDevice = async (deviceId) => {
    try {
      const response = await authFetch(`${API_URL}/api/securesphere/surveillance-iot/threats/scan/${deviceId}`, {
        method: 'POST'
      });
      const data = await response.json();
      alert(`Scan complete: ${data.threats_found} threats found`);
      fetchData();
    } catch (error) {
    }
  };

  const deleteDevice = async (deviceId) => {
    if (!window.confirm('Are you sure you want to delete this device?')) return;
    try {
      await authFetch(`${API_URL}/api/securesphere/surveillance-iot/devices/${deviceId}`, {
        method: 'DELETE'
      });
      fetchData();
      setSelectedDevice(null);
    } catch (error) {
    }
  };

  // New functions for auto-discovery and NetFlow
  const startNetworkScan = async (scanType = 'quick') => {
    setIsScanning(true);
    try {
      const response = await authFetch(`${API_URL}/api/securesphere/surveillance-iot/discovery/scan?scan_type=${scanType}`, {
        method: 'POST'
      });
      const data = await response.json();
      setScanResults(data);
    } catch (error) {
    } finally {
      setIsScanning(false);
    }
  };

  const registerDiscoveredDevice = async (scanId, deviceIp, deviceName, location) => {
    try {
      const response = await authFetch(
        `${API_URL}/api/securesphere/surveillance-iot/discovery/register-discovered?scan_id=${scanId}&device_ip=${deviceIp}&device_name=${encodeURIComponent(deviceName)}&location=${encodeURIComponent(location)}&owner_id=${encodeURIComponent(ownerId)}`,
        { method: 'POST' }
      );
      const data = await response.json();
      alert(`Device registered: ${data.device_name}`);
      fetchData();
    } catch (error) {
    }
  };

  const fetchNetFlowData = useCallback(async () => {
    try {
      const [anomaliesRes, botnetRes, vulnsRes, collectorRes] = await Promise.all([
        authFetch(`${API_URL}/api/securesphere/surveillance-iot/netflow/anomalies`),
        authFetch(`${API_URL}/api/securesphere/surveillance-iot/netflow/botnet-detection`),
        authFetch(`${API_URL}/api/securesphere/surveillance-iot/netflow/firmware-vulnerabilities`),
        authFetch(`${API_URL}/api/securesphere/surveillance-iot/collector/status`)
      ]);
      
      setAnomalies(await anomaliesRes.json());
      setBotnetStatus(await botnetRes.json());
      setVulnerabilities(await vulnsRes.json());
      setCollectorStatus(await collectorRes.json());
    } catch (error) {
    }
  }, []);

  const fetchTrafficPatterns = async (deviceId) => {
    try {
      const response = await authFetch(`${API_URL}/api/securesphere/surveillance-iot/netflow/traffic-patterns/${deviceId}`);
      const data = await response.json();
      setTrafficPatterns(data);
    } catch (error) {
    }
  };

  useEffect(() => {
    if (activeTab === 'netflow') {
      fetchNetFlowData();
    }
  }, [activeTab, fetchNetFlowData]);

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getCategoryColor = (category) => {
    const colors = {
      cameras: 'blue',
      smart_meters: 'yellow',
      dashcams: 'green'
    };
    return colors[category] || 'gray';
  };

  const getStatusColor = (status) => {
    return status === 'online' ? 'green' : status === 'offline' ? 'red' : 'yellow';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Surveillance IoT Dashboard</h2>
          <p className="text-gray-600">Monitor and protect cameras, smart meters, and dashcams</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button onClick={() => setShowRegisterModal(true)} className="bg-orange-600 hover:bg-orange-700">
            <Plus className="h-4 w-4 mr-2" />
            Add Device
          </Button>
        </div>
      </div>

      {/* Overview Stats */}
      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <div className="bg-white rounded-xl p-4 border shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Camera className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{overview.summary?.cameras || 0}</p>
                <p className="text-xs text-gray-500">Cameras</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Zap className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{overview.summary?.smart_meters || 0}</p>
                <p className="text-xs text-gray-500">Smart Meters</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Car className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{overview.summary?.dashcams || 0}</p>
                <p className="text-xs text-gray-500">Dashcams</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{overview.summary?.online || 0}</p>
                <p className="text-xs text-gray-500">Online</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{overview.security?.active_alerts || 0}</p>
                <p className="text-xs text-gray-500">Active Alerts</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Shield className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{overview.security?.average_score || 100}</p>
                <p className="text-xs text-gray-500">Security Score</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Tabs */}
      <div className="flex gap-2 border-b overflow-x-auto pb-2">
        <button
          onClick={() => setActiveTab('devices')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-t-lg whitespace-nowrap transition-all ${
            activeTab === 'devices' ? 'bg-orange-100 text-orange-700 border-b-2 border-orange-500' : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <Camera className="h-4 w-4" />
          <span className="font-medium text-sm">Devices</span>
        </button>
        <button
          onClick={() => setActiveTab('discovery')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-t-lg whitespace-nowrap transition-all ${
            activeTab === 'discovery' ? 'bg-blue-100 text-blue-700 border-b-2 border-blue-500' : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <Radar className="h-4 w-4" />
          <span className="font-medium text-sm">Auto-Discovery</span>
        </button>
        <button
          onClick={() => setActiveTab('netflow')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-t-lg whitespace-nowrap transition-all ${
            activeTab === 'netflow' ? 'bg-purple-100 text-purple-700 border-b-2 border-purple-500' : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <Network className="h-4 w-4" />
          <span className="font-medium text-sm">NetFlow Analytics</span>
        </button>
        <button
          onClick={() => setActiveTab('security')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-t-lg whitespace-nowrap transition-all ${
            activeTab === 'security' ? 'bg-red-100 text-red-700 border-b-2 border-red-500' : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <Bug className="h-4 w-4" />
          <span className="font-medium text-sm">Vulnerabilities</span>
        </button>
      </div>

      {/* DEVICES TAB */}
      {activeTab === 'devices' && (
        <>
          {/* Category Tabs */}
          <div className="flex gap-4 border-b overflow-x-auto pb-2">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`flex items-center gap-2 px-4 py-3 rounded-t-lg whitespace-nowrap transition-all ${
                  activeCategory === cat.id
                    ? `bg-${cat.color}-100 text-${cat.color}-700 border-b-2 border-${cat.color}-500`
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <cat.icon className="h-5 w-5" />
                <span className="font-medium">{cat.name}</span>
                <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                  activeCategory === cat.id ? `bg-${cat.color}-200` : 'bg-gray-200'
                }`}>
                  {overview?.summary?.[cat.id] || 0}
                </span>
              </button>
            ))}
          </div>

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Devices List */}
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">
              {categories.find(c => c.id === activeCategory)?.name} ({devices.length})
            </h3>
            <div className="flex gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search devices..."
                  className="pl-9 pr-4 py-2 border rounded-lg text-sm w-48"
                />
              </div>
            </div>
          </div>

          {devices.length === 0 ? (
            <div className="bg-white rounded-xl border shadow-sm p-12 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                {React.createElement(categories.find(c => c.id === activeCategory)?.icon || Camera, {
                  className: "h-8 w-8 text-gray-400"
                })}
              </div>
              <h4 className="font-medium text-gray-700 mb-2">No {categories.find(c => c.id === activeCategory)?.name} registered</h4>
              <p className="text-sm text-gray-500 mb-4">Add your first device to start monitoring</p>
              <Button onClick={() => setShowRegisterModal(true)} className="bg-orange-600 hover:bg-orange-700">
                <Plus className="h-4 w-4 mr-2" />
                Add Device
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {devices.map((device) => (
                <div
                  key={device.id}
                  onClick={() => viewDeviceDetails(device)}
                  className={`bg-white rounded-xl border shadow-sm p-4 cursor-pointer hover:shadow-md transition-all ${
                    selectedDevice?.id === device.id ? 'ring-2 ring-orange-500' : ''
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 bg-${getCategoryColor(device.category)}-100 rounded-lg`}>
                        {React.createElement(
                          deviceTypeOptions[device.category]?.find(t => t.id === device.device_type)?.icon || Camera,
                          { className: `h-5 w-5 text-${getCategoryColor(device.category)}-600` }
                        )}
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">{device.device_name}</h4>
                        <p className="text-xs text-gray-500">{device.manufacturer} {device.model}</p>
                      </div>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      device.status === 'online' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {device.status}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                    <div>
                      <p className="text-xs text-gray-500">Location</p>
                      <p className="text-gray-700 truncate">{device.location || 'Not set'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Security Score</p>
                      <p className={`font-medium ${
                        device.security_score >= 80 ? 'text-green-600' :
                        device.security_score >= 50 ? 'text-yellow-600' : 'text-red-600'
                      }`}>{device.security_score || 100}/100</p>
                    </div>
                    {device.category === 'cameras' && (
                      <>
                        <div>
                          <p className="text-xs text-gray-500">Resolution</p>
                          <p className="text-gray-700">{device.resolution}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Stream</p>
                          <p className="text-gray-700 uppercase">{device.stream_protocol}</p>
                        </div>
                      </>
                    )}
                    {device.category === 'smart_meters' && (
                      <>
                        <div>
                          <p className="text-xs text-gray-500">Reading</p>
                          <p className="text-gray-700">{device.current_reading} {device.reading_unit}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Communication</p>
                          <p className="text-gray-700 uppercase">{device.communication_type?.replace('_', ' ')}</p>
                        </div>
                      </>
                    )}
                    {device.category === 'dashcams' && (
                      <>
                        <div>
                          <p className="text-xs text-gray-500">Vehicle</p>
                          <p className="text-gray-700">{device.license_plate || device.vehicle_id}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Storage</p>
                          <p className="text-gray-700">{device.storage_used_percent}% used</p>
                        </div>
                      </>
                    )}
                  </div>

                  <div className="flex items-center justify-between pt-3 border-t">
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <Wifi className="h-3 w-3" />
                      <span>{formatBytes(device.bytes_in_24h || 0)}/24h</span>
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={(e) => { e.stopPropagation(); scanDevice(device.id); }}
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                        title="Scan for threats"
                      >
                        <Shield className="h-4 w-4" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); deleteDevice(device.id); }}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                        title="Delete device"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Device Details / Alerts Panel */}
        <div className="space-y-4">
          <DeviceDetailPanel
            selectedDevice={selectedDevice}
            monitoringData={monitoringData}
            alerts={alerts}
            categoryData={categoryData}
            onCloseDetail={() => setSelectedDevice(null)}
            formatBytes={formatBytes}
          />
        </div>
      </div>
        </>
      )}

      {/* AUTO-DISCOVERY TAB */}
      {activeTab === 'discovery' && (
        <DiscoveryTab
          scanResults={scanResults}
          isScanning={isScanning}
          startNetworkScan={startNetworkScan}
          registerDiscoveredDevice={registerDiscoveredDevice}
        />
      )}

      {/* NETFLOW ANALYTICS TAB */}
      {activeTab === 'netflow' && (
        <SurveillanceNetFlowTab
          collectorStatus={collectorStatus}
          anomalies={anomalies}
          botnetStatus={botnetStatus}
          formatBytes={formatBytes}
          fetchNetFlowData={fetchNetFlowData}
        />
      )}

      {/* VULNERABILITIES TAB */}
      {activeTab === 'security' && (
        <SurveillanceSecurityTab
          vulnerabilities={vulnerabilities}
          fetchNetFlowData={fetchNetFlowData}
        />
      )}

      {/* Register Modal */}
      {showRegisterModal && (
        <RegisterDeviceModal
          activeCategory={activeCategory}
          categories={categories}
          registerForm={registerForm}
          setRegisterForm={setRegisterForm}
          registerDevice={registerDevice}
          onClose={() => setShowRegisterModal(false)}
          deviceTypeOptions={deviceTypeOptions}
          manufacturers={manufacturers}
        />
      )}
    </div>
  );
};

export default SurveillanceIoTDashboard;
